// Module-scope state shared between the "+ Add post" trigger site
// (CreatePostButton) and the Pusher completion site (CalendarPage):
//
// 1. `triggeredAt` — creativeId → { startedAt, channel }. Used to attach
//    duration_ms + channel to the CALENDAR_POST_GENERATION_COMPLETED
//    analytics event, and as the "we initiated this" signal that gates
//    user-facing toasts to calendar-originated generations only.
//
// 2. `finalized` — set of creativeIds for which we've already received a
//    CreativeGenerationComplete Pusher event. Consumed by CalendarDayCell's
//    `isGenerating` logic so the snake-border overlay disappears as soon
//    as generation is actually done, even if the backend couldn't produce
//    an image (thumbnailUrl remains null).
//
// Both live outside React state / Zustand because they're transient, tiny,
// and cross-component. Entries auto-evict (on consumption for the map, via
// TTL timer for both) so nothing can grow unbounded over a long session.

const TTL_MS = 15 * 60 * 1000 // 15 min — matches generation worst-case

interface TriggerRecord {
  startedAt: number
  channel?: string
}

const triggeredAt = new Map<string, TriggerRecord>()

export function markCalendarGenerationTriggered(creativeId: string, channel?: string): void {
  triggeredAt.set(creativeId, { startedAt: Date.now(), channel })
  // Best-effort self-clean if Pusher never delivers. Safe to call delete on a
  // key that was already consumed — it's a no-op.
  setTimeout(() => {
    triggeredAt.delete(creativeId)
  }, TTL_MS)
}

export interface ConsumedTrigger {
  durationMs: number
  channel?: string
}

export function consumeCalendarGenerationTrigger(creativeId: string): ConsumedTrigger | undefined {
  const record = triggeredAt.get(creativeId)
  if (record == null) return undefined
  triggeredAt.delete(creativeId)
  // Clamp rather than discard when the elapsed time exceeds the TTL. The
  // TTL exists so the Map self-cleans if Pusher never fires, NOT as a
  // signal that the event is no longer calendar-initiated. Returning
  // `undefined` here used to drop the CALENDAR_POST_GENERATION_COMPLETED
  // event (and the user-facing "failed to generate" toast) for any run
  // that took longer than 15 min, silently losing signal in the funnel.
  // Clamp the duration so warehouse queries aren't skewed by outliers.
  const rawDurationMs = Date.now() - record.startedAt
  const durationMs = Math.min(Math.max(rawDurationMs, 0), TTL_MS)
  return { durationMs, channel: record.channel }
}

const finalized = new Set<string>()

export function markCreativeGenerationFinalized(creativeId: string): void {
  finalized.add(creativeId)
  setTimeout(() => {
    finalized.delete(creativeId)
  }, TTL_MS)
}

export function isCreativeGenerationFinalized(creativeId: string | undefined | null): boolean {
  if (!creativeId) return false
  return finalized.has(creativeId)
}
